import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CloseButton,
    Flex,
    Link,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Text,
    useBreakpointValue,
    useTheme
} from '@chakra-ui/react';
import { ContractContext } from 'ts/common/contract/types';
import { useTranslate } from 'ts/common/hooks';
import ModernButton from 'ts/studio/common/ModernButton';

const SVG_VIEWBOX = '0 0 400 256';
const MIN_SIGNATURE_LINES = 5;

interface SignatureModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAcceptAndSign: (signaturePaths: string, svgViewbox: string) => void;
    translationKeyBase?: string;
    context?: ContractContext;
}

const SignatureModal: React.FC<SignatureModalProps> = ({
    isOpen,
    context,
    onClose,
    onAcceptAndSign,
    translationKeyBase = 'signatureModal'
}) => {
    const [signature, setSignature] = useState('');
    const [isDrawing, setIsDrawing] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const svgRef = useRef<SVGSVGElement>(null);
    const t = useTranslate(translationKeyBase);
    const theme = useTheme();

    // Check if signature has enough substance (minimum number of line segments)
    const hasValidSignature =
        signature && (signature.match(/L/g) || []).length >= MIN_SIGNATURE_LINES;
    const isAcceptButtonDisabled = !hasValidSignature || !isChecked;
    const isMobile = useBreakpointValue({ base: true, md: false });
    const isClientContext =
        context === ContractContext.CLIENT_BOOKING_CONTRACT ||
        context === ContractContext.CLIENT_STANDALONE_CONTRACT;

    const getCoordinates = (
        event: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>
    ) => {
        if (!svgRef.current) {
            return '';
        }

        const point = svgRef.current.createSVGPoint();

        if ('touches' in event.nativeEvent) {
            const touchEvent = event.nativeEvent as TouchEvent;
            point.x = touchEvent.targetTouches[0].clientX;
            point.y = touchEvent.targetTouches[0].clientY;
        } else {
            const mouseEvent = event.nativeEvent as MouseEvent;
            point.x = mouseEvent.clientX;
            point.y = mouseEvent.clientY;
        }

        // Transform the point from screen coordinates to SVG coordinates
        const ctm = svgRef.current.getScreenCTM();

        if (ctm) {
            const transformedPoint = point.matrixTransform(ctm.inverse());
            return `${transformedPoint.x},${transformedPoint.y}`;
        }

        return '';
    };

    const handleMouseDown = (
        event: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>
    ) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDrawing(true);
        const coords = getCoordinates(event);

        if (coords) {
            setSignature((prev) => `${prev} M${coords}`);
        }
    };

    const handleMouseMove = (
        event: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>
    ) => {
        if (!isDrawing) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        const coords = getCoordinates(event);

        if (coords) {
            setSignature((prev) => `${prev}L${coords}`);
        }
    };

    const handleMouseUp = (
        event: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>
    ) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDrawing(false);
    };

    // Add event listeners to handle mouse leaving the SVG area
    useEffect(() => {
        const svg = svgRef.current;

        if (!svg) {
            return;
        }

        const handleMouseLeave = () => {
            setIsDrawing(false);
        };

        svg.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            svg.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, []);

    const clearSignature = () => {
        setSignature('');
    };

    const handleAcceptAndSign = () => {
        onAcceptAndSign(signature, SVG_VIEWBOX);
        onClose();
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsChecked(e.target.checked);
    };

    const ButtonComponent = isClientContext ? Button : ModernButton;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={isMobile ? 'sm' : 'xl'} isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="16px">
                <ModalHeader
                    color="brand.primary"
                    fontSize="20px"
                    fontWeight="600"
                    fontFamily="open-sans"
                    paddingY={isMobile ? '16px' : '24px'}
                    paddingX={isMobile ? '24px' : '56px'}
                    borderBottom="none"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    {t('signature')}
                    <CloseButton
                        onClick={onClose}
                        border="none"
                        color="gray.500"
                        data-testid="signature-modal-close-button"
                        _hover={{ color: 'brand.primary' }}
                        sx={{
                            '&:focus': {
                                color: 'gray.500'
                            }
                        }}
                        cursor="pointer"
                        aria-label={t('cancel')}
                    />
                </ModalHeader>
                <ModalBody
                    paddingTop="0"
                    paddingInlineStart={isMobile ? '16px' : '24px'}
                    paddingInlineEnd={isMobile ? '16px' : '24px'}
                    paddingBottom={isMobile ? '24px' : '32px'}
                    borderRadius="16px"
                >
                    <Box position="relative">
                        <Box
                            data-testid="signature-svg"
                            as="svg"
                            width="100%"
                            height={isMobile ? '180px' : '256px'}
                            border="1px solid #E2E2E2"
                            borderRadius="0"
                            ref={svgRef}
                            viewBox={SVG_VIEWBOX}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseOut={handleMouseUp}
                            onTouchStart={handleMouseDown}
                            onTouchMove={handleMouseMove}
                            onTouchEnd={handleMouseUp}
                            style={{ touchAction: 'none' }}
                            backgroundColor={
                                theme.isLightColorScheme === false ? '#B3C1C6' : '#F9F9F9'
                            }
                            position="relative"
                            aria-label={t('signatureDrawingArea')}
                            role="application"
                            aria-roledescription={t('signatureDrawingArea')}
                        >
                            <line
                                x1="15"
                                y1="241"
                                x2="385"
                                y2="241"
                                stroke={
                                    theme.isLightColorScheme === false
                                        ? theme.colors.gray[200]
                                        : theme.colors.gray[300]
                                }
                                strokeWidth="2"
                            />
                            <path
                                d={signature}
                                fill="none"
                                stroke="black"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                pointerEvents="none"
                            />
                        </Box>
                        <Button
                            position="absolute"
                            top={isMobile ? '8px' : '16px'}
                            right={isMobile ? '8px' : '16px'}
                            onClick={clearSignature}
                            variant="ghost"
                            color={
                                theme.isLightColorScheme === false
                                    ? theme.colors.gray[200]
                                    : theme.colors.gray[300]
                            }
                            fontSize="14px"
                            height="auto"
                            padding="0"
                            _hover={{ bg: 'transparent', color: '#4A154B' }}
                            cursor="pointer"
                        >
                            {t('clear')}
                        </Button>
                    </Box>
                    <Checkbox
                        marginTop={isMobile ? '16px' : '24px'}
                        spacing="12px"
                        isChecked={isChecked}
                        onChange={handleCheckboxChange}
                        aria-required="true"
                        sx={{
                            '[class*="chakra-checkbox__control"]': {
                                bg: 'white',
                                _checked: {
                                    bg: 'brand.primary',
                                    borderColor: 'brand.primary'
                                }
                            }
                        }}
                    >
                        <Text fontSize="14px" whiteSpace="nowrap">
                            {t('acceptAndSignDescription')}
                            <Link
                                href="https://www.shootproof.com/legal/terms-of-use/"
                                target="_blank"
                                color="brand.primary"
                                display="inline"
                                _focus={{
                                    boxShadow: 'none'
                                }}
                                _focusVisible={{
                                    boxShadow: 'outline'
                                }}
                            >
                                {t('termsOfUse')}
                            </Link>
                            {t('and')}
                            <Link
                                href="https://www.shootproof.com/legal/privacy-policy/"
                                target="_blank"
                                color="brand.primary"
                                display="inline"
                                _focus={{
                                    boxShadow: 'none'
                                }}
                                _focusVisible={{
                                    boxShadow: 'outline'
                                }}
                            >
                                {t('privacyPolicy')}
                            </Link>
                            .
                        </Text>
                    </Checkbox>
                    <Flex gap={isMobile ? '8px' : '16px'} marginTop={isMobile ? '24px' : '32px'}>
                        <ButtonComponent
                            onClick={onClose}
                            variant="outline primary"
                            width="100%"
                            height={isMobile ? '44px' : 'auto'}
                        >
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent
                            onClick={handleAcceptAndSign}
                            variant="primary"
                            width="100%"
                            isDisabled={isAcceptButtonDisabled}
                            cursor={isAcceptButtonDisabled ? 'not-allowed' : 'pointer'}
                            aria-disabled={isAcceptButtonDisabled}
                            height={isMobile ? '44px' : 'auto'}
                        >
                            {t('acceptAndSign')}
                        </ButtonComponent>
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default SignatureModal;
